import React, { useCallback, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import { HOVER_ROW_OVERLAY_FILL } from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  RATING_COLORS,
  SENTIMENT_ELEMENT_COLORS,
} from 'features/charts-v2/constants'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import { useYAxisLabelStep } from 'features/charts-v2/hooks/use-y-axis-label-step'
import { useYAxisTickRenderer } from 'features/charts-v2/hooks/UseAxisTickRenderer'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { DEFAULT_SERIES_COLOR } from 'features/charts-v2/services/series-color-palette'
import { type MetricType } from 'features/charts-v2/types'
import { resolveBarValueDomain } from 'features/charts-v2/utils/axis-calculations'
import {
  calculateHorizontalBarYAxisWidth,
  DEFAULT_X_AXIS_HEIGHT_PX,
  formatMetricAxisNumber,
  getMetricAxisDomain,
  HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS,
  MAX_Y_AXIS_LABELS,
} from 'features/charts-v2/utils/axis-tick-utils'
import { categoryRow } from 'features/charts-v2/utils/build-hover-annotation'
import {
  getMetricsLabel,
  getMetricUnitLabel,
} from 'features/charts-v2/utils/metric-format-utils'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

interface StackedBarDataItem {
  name: string
  positive?: number
  neutral?: number
  negative?: number
  total?: number
  [key: string]: string | number | undefined
}

interface StackedBarChartProps {
  data: StackedBarDataItem[]
  selectedMetrics: MetricType[]
  onDataPointHover?: (dataPoint: string | null) => void
  onSegmentHover?: (category: string | null) => void
  breakdownCategories?: string[] // Optional: categories for generic stacking
  breakdownCategoryLabels?: string[]
  onSegmentClick?: (
    segmentData: {
      primaryBreakdown: string // The y-axis category (e.g., theme name)
      stackingBreakdown?: string // The stacking category (e.g., sentiment)
      value: number
      payload: StackedBarDataItem
    },
    event: React.MouseEvent,
  ) => void
  isRatingStacking?: boolean
  isSentimentStacking?: boolean
  /** When set, draws a highlight band behind the matching row. Direction controls color. */
  highlightedRow?: { name: string; direction: 'leading' | 'lagging' }
  fontSizePx?: number
  showXAxisLabel?: boolean
  showYAxisLabel?: boolean
  showDatapointLabels?: boolean
  xAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  yAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  yAxisDomain?: [number, number]
  xAxisDomain?: [number, number]
}

/**
 * Format number for display in labels
 * - Sentiment/Rating: always 2 decimal places
 * - Whole numbers: no decimals (e.g., 5)
 * - Decimals: max 2 decimal places (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number, metricType?: MetricType): string => {
  if (metricType === 'sentiment' || metricType === 'rating') {
    return value.toFixed(2)
  }
  if (Number.isInteger(value)) {
    return value.toString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toString()
}

/**
 * Safely format a category label for display
 * Handles non-string values (numbers, UUIDs) by converting to string first
 * Then capitalizes the first letter
 */
const formatCategoryLabel = (category: string | number | undefined): string => {
  if (category === null || category === undefined) return ALIAS_FOR_NULL_VALUES
  const str = String(category)
  if (str.length === 0) return ALIAS_FOR_NULL_VALUES
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Custom label component for horizontal stacked bar total
 * Displays the total value to the right of the stacked bar
 * Note: This is called on the last (rightmost) segment, so we need to find the right edge of the entire stack
 */
const CustomStackedBarLabel = React.memo<{
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  payload?: StackedBarDataItem
  categories?: string[]
  fontSizePx?: number
  index?: number
  metricType?: MetricType
}>(
  ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    payload,
    categories = [],
    fontSizePx = 13,
    metricType,
  }) => {
    // Calculate total from all categories
    if (!payload || !categories || categories.length === 0) return null

    const total = categories.reduce((sum, category) => {
      return sum + ((payload[category] as number) || 0)
    }, 0)

    // Don't render label if total is 0
    if (total === 0) return null

    // For horizontal stacked bars, position the label to the right of the entire stack
    // x + width gives us the right edge of the last segment
    return (
      <text
        x={x + width + 5}
        y={y + height / 2}
        fill="#333"
        fontSize={fontSizePx}
        textAnchor="start"
        dominantBaseline="middle"
        pointerEvents="none"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {formatLabelValue(total, metricType)}
      </text>
    )
  },
)

CustomStackedBarLabel.displayName = 'CustomStackedBarLabel'

/**
 * Segment percentage label component for displaying percentage inside each bar segment
 * Shows what % each segment represents of the row total
 * Used with LabelList component which provides proper data access
 */
const SegmentPercentageLabel = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  index,
  data,
  category,
  categories,
  fontSizePx = 13,
}: {
  x?: number
  y?: number
  width?: number
  height?: number
  _value?: number
  index?: number
  data?: StackedBarDataItem[]
  category: string
  categories: string[]
  fontSizePx?: number
}): JSX.Element | null => {
  if (!data || !categories.length || width < 30 || index === undefined) {
    return null
  }

  const dataPoint = data[index]
  if (!dataPoint) return null

  const total = categories.reduce((sum, cat) => {
    return sum + ((dataPoint[cat] as number) || 0)
  }, 0)
  const segmentValue = (dataPoint[category] as number) || 0

  if (total === 0 || segmentValue === 0) return null

  const percentage = Math.round((segmentValue / total) * 100)
  if (percentage < 5) return null

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#fff"
      fontSize={fontSizePx}
      textAnchor="middle"
      dominantBaseline="middle"
      pointerEvents="none"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 500,
      }}
    >
      {percentage}%
    </text>
  )
}

const sentimentColorFor = (cat: string): string =>
  SENTIMENT_ELEMENT_COLORS[
    String(cat).toLowerCase() as keyof typeof SENTIMENT_ELEMENT_COLORS
  ] ?? DEFAULT_SERIES_COLOR

const ratingColorFor = (cat: string): string => {
  const match = String(cat).match(/^(\d+)/)
  if (match) {
    const n = parseInt(match[1]!, 10)
    if (n >= 1 && n <= 5) return RATING_COLORS[String(n)]!
  }
  return DEFAULT_SERIES_COLOR
}

/**
 * Generate colors for generic breakdown categories using the canonical primary palette
 */
const generateCategoryColors = (
  categories: string[],
): { [key: string]: string } => {
  const colors: { [key: string]: string } = {}
  categories.forEach((category, index) => {
    colors[category] = chartConfig.getColor('primary', index)
  })
  return colors
}

/**
 * Styled container for the chart. Hover dimming is handled imperatively by
 * Container.tsx via data-row-name / data-segment-category attributes — no CSS
 * opacity rules here to prevent double-application and opacity compounding.
 */
const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
`

/**
 * Context used to pass the currently-hovered stacked segment category from the
 * chart event handlers to the tooltip card, WITHOUT re-rendering the <BarChart>
 * subtree. The Provider re-renders only the tooltip (a context consumer), leaving
 * the axes and bars untouched — eliminating the X-axis flicker that occurred when
 * the hovered category was stored as local component state.
 */
const HoveredCategoryContext = React.createContext<string | null>(null)

/**
 * Tooltip card for the stacked bar chart. Reads the focused segment from context
 * so that a segment-hover re-render never touches the chart SVG.
 */
const StackedBarTooltip: React.FC<{
  active?: boolean
  payload?: Array<{ payload: StackedBarDataItem }>
  label?: string | number
  categories: string[]
  categoryLabels: string[]
  colors: { [key: string]: string }
  isSentimentChart: boolean
  isRatingStacking: boolean
  primaryMetricType?: MetricType
}> = ({
  active,
  payload: tooltipPayload,
  label,
  categories,
  categoryLabels,
  colors,
  isSentimentChart,
  isRatingStacking,
  primaryMetricType,
}) => {
  // Reads the hovered segment from context — re-renders independently of the chart.
  const hoveredCategory = React.useContext(HoveredCategoryContext)
  const labelByCategory = Object.fromEntries(
    categories.map((category, index) => [
      category,
      categoryLabels[index] ?? category,
    ]),
  )

  if (!(active && tooltipPayload && tooltipPayload.length)) return null
  const first = tooltipPayload[0]
  if (!first) return null
  const rowData = first.payload

  const total = categories.reduce((sum, category) => {
    return sum + ((rowData[category] as number) || 0)
  }, 0)

  const sortedCategories = categories
    .filter((category) => ((rowData[category] as number) || 0) > 0)
    .sort((a, b) => {
      if (isSentimentChart) {
        const sentimentOrder: { [key: string]: number } = {
          positive: 0,
          neutral: 1,
          negative: 2,
        }
        const rank = (cat: string): number =>
          sentimentOrder[labelByCategory[cat]!.toLowerCase()] ?? 99
        return rank(String(a)) - rank(String(b))
      }
      if (isRatingStacking) {
        const extractRating = (cat: string): number => {
          const match = labelByCategory[cat]!.match(/^(\d)/)
          return match?.[1] ? parseInt(match[1], 10) : 0
        }
        return extractRating(b) - extractRating(a)
      }
      return ((rowData[b] as number) || 0) - ((rowData[a] as number) || 0)
    })

  const unitSuffix = primaryMetricType
    ? ` ${getMetricUnitLabel(primaryMetricType)}`
    : ''

  // Appends "-star" to rating category labels so "5" becomes "5-star".
  // Null/No-Value ratings keep the plain alias — "No Value-star" reads wrong.
  const formatRowLabel = (category: string | number | undefined): string => {
    const formatted = formatCategoryLabel(category)
    return isRatingStacking && formatted !== ALIAS_FOR_NULL_VALUES
      ? `${formatted}-star`
      : formatted
  }

  const rows = sortedCategories.map((category) => {
    const value = (rowData[category] as number) || 0
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0'
    const formattedValue =
      primaryMetricType === 'sentiment' || primaryMetricType === 'rating'
        ? value.toFixed(2)
        : value.toLocaleString()
    return categoryRow(
      formatRowLabel(labelByCategory[category]),
      `${formattedValue}${unitSuffix}`,
      `${percentage}%`,
      colors[category] ?? null,
    )
  })

  const totalFormatted =
    primaryMetricType === 'sentiment' || primaryMetricType === 'rating'
      ? total.toFixed(2)
      : total.toLocaleString()

  return (
    <ChartDatapointHoverAnnotation
      title={String(label)}
      rows={rows}
      footer={{
        label: 'Total',
        value: `${totalFormatted}${unitSuffix}`,
        percentage: null,
        color: null,
      }}
      focusedField={
        hoveredCategory
          ? formatRowLabel(labelByCategory[hoveredCategory])
          : null
      }
    />
  )
}

/**
 * StackedBarChart component for displaying breakdown data
 * Can show sentiment breakdown or any generic breakdown categories
 */
const StackedBarChartInner: React.FC<StackedBarChartProps> = ({
  data,
  selectedMetrics,
  onDataPointHover,
  onSegmentHover,
  breakdownCategories,
  breakdownCategoryLabels,
  isRatingStacking = false,
  isSentimentStacking = false,
  onSegmentClick,
  highlightedRow,
  fontSizePx = 13,
  showXAxisLabel = true,
  showYAxisLabel = true,
  showDatapointLabels = true,
  yAxisOverflow = 'expand',
  xAxisDomain,
}) => {
  const colorMapProp = useBreakdownColorMap()
  // Detect if metric is sentiment or rating
  const primaryMetricType = selectedMetrics[0]

  const categories = useMemo<string[]>(() => {
    const provided = breakdownCategories ?? []
    if (provided.length > 0) return provided
    if (data.length === 0) return []
    const firstItem = data[0]!
    return Object.keys(firstItem).filter(
      (key) => key !== 'name' && typeof firstItem[key] === 'number',
    )
  }, [breakdownCategories, data])

  const categoryLabels = useMemo(
    () =>
      categories.map(
        (category, index) => breakdownCategoryLabels?.[index] ?? category,
      ),
    [categories, breakdownCategoryLabels],
  )

  const isSentimentChart =
    categoryLabels.length === 3 &&
    categoryLabels.some((label) => label.toLowerCase() === 'positive') &&
    categoryLabels.some((label) => label.toLowerCase() === 'neutral') &&
    categoryLabels.some((label) => label.toLowerCase() === 'negative')

  const categoryLabelByKey = useMemo(
    () =>
      Object.fromEntries(
        categories.map((category, index) => [
          category,
          categoryLabels[index] ?? category,
        ]),
      ),
    [categories, categoryLabels],
  )

  const colors = useMemo<{ [key: string]: string }>(() => {
    const f =
      isSentimentChart || isSentimentStacking
        ? (c: string) => sentimentColorFor(c)
        : isRatingStacking
          ? (c: string) => ratingColorFor(String(c))
          : null
    if (f) {
      return Object.fromEntries(
        categories.map((category) => [
          category,
          f(categoryLabelByKey[category]!),
        ]),
      )
    }
    // Generic breakdown: prefer the pre-computed stable map from Container so
    // colors survive filter changes; fall back to positional assignment.
    if (colorMapProp.size > 0) {
      return Object.fromEntries(
        categories.map((c, i) => [
          c,
          colorMapProp.get(categoryLabelByKey[c]!) ??
            chartConfig.getColor('primary', i),
        ]),
      )
    }
    return generateCategoryColors(categories)
  }, [
    isSentimentChart,
    isSentimentStacking,
    isRatingStacking,
    categories,
    categoryLabelByKey,
    colorMapProp,
  ])

  // Hovering a stacked segment sets this, which re-renders ONLY the tooltip card
  // (via HoveredCategoryContext) — NOT the <BarChart>. The chart element is memoized
  // below with hoveredCategory deliberately excluded from its deps, so the axes and
  // bars are never reflowed on hover.
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)

  // Extract labels from chart data for Y-axis width calculation
  const labels = data.map((d) => String(d.name ?? ''))

  // Calculate Y-axis width by measuring text after applying overflow mode,
  // mirroring V1's auto-sizing behaviour (clamped to [100, 400] px).
  const yAxisWidth = useMemo(
    () => calculateHorizontalBarYAxisWidth(labels, yAxisOverflow, fontSizePx),
    [labels, yAxisOverflow, fontSizePx],
  )

  // Stable Recharts props — these must not change identity on re-renders,
  // otherwise Recharts remounts axis label <text> elements (flicker).

  const chartMargin = useMemo(
    () => ({ top: 20, right: 30, left: 20, bottom: 20 }),
    [],
  )

  // Cap y-axis labels at MAX_Y_AXIS_LABELS regardless of available height
  const { containerRef, step } = useYAxisLabelStep({
    itemCount: data.length,
    fontSizePx,
    maxLabels: MAX_Y_AXIS_LABELS,
    chromeHeightPx:
      chartMargin.top + chartMargin.bottom + DEFAULT_X_AXIS_HEIGHT_PX,
  })

  // Use hook to create a stable tick renderer for Y-axis (category labels).
  // `step` is forwarded so thinned-out labels fade (interval={0} below keeps
  // every tick mounted) instead of Recharts mounting/unmounting them.
  const yAxisTickRenderer = useYAxisTickRenderer({
    fontSizePx,
    overflowMode: yAxisOverflow,
    truncateAtChars: HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS,
    wrapAtPx: yAxisWidth - 16,
    step,
  })

  const handleMouseMove = useCallback(
    (chartData: { activeLabel?: string }) => {
      if (chartData && chartData.activeLabel) {
        onDataPointHover?.(chartData.activeLabel)
      }
    },
    [onDataPointHover],
  )

  // Clear the focused segment highlight when the cursor leaves the chart.
  const handleMouseLeave = useCallback(() => {
    onDataPointHover?.(null)
    onSegmentHover?.(null)
    setHoveredCategory(null)
  }, [onDataPointHover, onSegmentHover])

  const xAxisTick = useMemo(() => ({ fontSize: fontSizePx }), [fontSizePx])

  const xAxisTickFormatter = useCallback(
    (value: number) => formatMetricAxisNumber(value, primaryMetricType),
    [primaryMetricType],
  )

  const xAxisLabel = useMemo(
    () =>
      showXAxisLabel
        ? {
            value: getMetricsLabel(selectedMetrics),
            position: 'insideTop' as const,
            offset: -5,
            style: {
              textAnchor: 'middle',
              fill: '#666',
              fontSize: fontSizePx,
            },
          }
        : undefined,
    [showXAxisLabel, selectedMetrics, fontSizePx],
  )

  const yAxisLabel = useMemo(
    () =>
      showYAxisLabel
        ? {
            value: '',
            angle: -90,
            position: 'insideLeft' as const,
            style: {
              textAnchor: 'middle',
              fill: '#666',
              fontSize: fontSizePx,
            },
          }
        : undefined,
    [showYAxisLabel, fontSizePx],
  )

  // Mirrors isRatingStacking — applies regardless of data-encoding shape.
  const tooltipIsSentimentChart = !!isSentimentChart || isSentimentStacking

  // Stable — hoveredCategory is NOT a dep. focusedField comes from context inside
  // StackedBarTooltip, so the chart element never needs to re-render on hover.
  const tooltipContent = useCallback(
    (props: {
      active?: boolean
      payload?: Array<{ payload: StackedBarDataItem }>
      label?: string | number
    }) => (
      <StackedBarTooltip
        {...props}
        categories={categories}
        categoryLabels={categoryLabels}
        colors={colors}
        isSentimentChart={tooltipIsSentimentChart}
        isRatingStacking={isRatingStacking}
        primaryMetricType={primaryMetricType}
      />
    ),
    [
      categories,
      categoryLabels,
      colors,
      tooltipIsSentimentChart,
      isRatingStacking,
      primaryMetricType,
    ],
  )

  const tooltipCursor = useMemo(() => ({ fill: HOVER_ROW_OVERLAY_FILL }), [])

  // Memoize the effective X-axis domain so it doesn't produce a new array reference
  // on hover-driven re-renders of StackedBarChartInner.
  const effectiveXDomainMemo = useMemo(
    () =>
      // Guarded against a degenerate [v, v] domain (e.g. the all-zero synced
      // edge) — Recharts has no handling for zero-width explicit domains.
      resolveBarValueDomain(
        xAxisDomain || getMetricAxisDomain(primaryMetricType),
        primaryMetricType,
      ),
    [xAxisDomain, primaryMetricType],
  )

  const totalValue = useCallback(
    (row: StackedBarDataItem) =>
      categories.reduce(
        (sum, category) => sum + (Number(row[category]) || 0),
        0,
      ),
    [categories],
  )

  // Render the full stack as one Recharts bar. Recharts then computes one
  // shared row rectangle, while we partition its width into exact segments.
  // This avoids both its sparse stackedData crash and grouped-bar Y drift.
  const stackedBarShape = useMemo(
    () =>
      ((props: Record<string, unknown>) => {
        const payload = props['payload'] as StackedBarDataItem | undefined
        const x = Number(props['x']) || 0
        const y = Number(props['y']) || 0
        const width = Number(props['width']) || 0
        const height = Number(props['height']) || 0
        const rowIndex = Number(props['index']) || 0
        if (!payload) return <g />

        const total = totalValue(payload)
        if (total <= 0) return <g />

        let segmentX = x
        return (
          <g>
            {categories.map((category, categoryIndex) => {
              const value = Number(payload[category]) || 0
              const segmentWidth = (width * value) / total
              const isLastNonZero =
                categories.findLast(
                  (candidate) => (Number(payload[candidate]) || 0) > 0,
                ) === category
              const currentX = segmentX
              segmentX += segmentWidth

              if (value <= 0) return null

              return (
                <React.Fragment key={category}>
                  <Rectangle
                    {...props}
                    x={currentX}
                    y={y}
                    width={segmentWidth}
                    height={height}
                    fill={colors[category]}
                    radius={isLastNonZero ? [0, 12, 12, 0] : 0}
                    data-row-name={payload.name}
                    data-segment-category={
                      categoryLabels[categoryIndex] ?? category
                    }
                    onClick={(event: React.MouseEvent) =>
                      onSegmentClick?.(
                        {
                          primaryBreakdown: payload.name,
                          stackingBreakdown: categoryLabelByKey[category],
                          value,
                          payload,
                        },
                        event,
                      )
                    }
                    onMouseEnter={() => {
                      setHoveredCategory(category)
                      onSegmentHover?.(categoryLabelByKey[category]!)
                    }}
                    onMouseLeave={() => {
                      setHoveredCategory(null)
                      onSegmentHover?.(null)
                    }}
                  />
                  {showDatapointLabels && (
                    <SegmentPercentageLabel
                      x={currentX}
                      y={y}
                      width={segmentWidth}
                      height={height}
                      index={rowIndex}
                      data={data}
                      category={category}
                      categories={categories}
                      fontSizePx={fontSizePx}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </g>
        )
      }) as never,
    [
      categories,
      categoryLabels,
      categoryLabelByKey,
      colors,
      data,
      fontSizePx,
      onSegmentClick,
      onSegmentHover,
      showDatapointLabels,
      totalValue,
    ],
  )

  // Memoized chart element — hoveredCategory is NOT in the dep list.
  // When a segment is hovered, hoveredCategory state changes, re-rendering
  // StackedBarChartInner. Because chartElement's deps are all stable across
  // a hover, React reuses the same element reference and bails out of
  // reconciling the <BarChart> subtree — axes and bars are never reflowed.
  // focusedField propagates to StackedBarTooltip via HoveredCategoryContext.
  const chartElement = useMemo(
    () => (
      <ResponsiveContainer width="100%" height="100%" debounce={100}>
        <BarChart
          data={data}
          layout="vertical"
          barGap={0}
          margin={chartMargin}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />

          {/* X-axis at top */}
          <XAxis
            type="number"
            orientation="top"
            tick={xAxisTick}
            tickFormatter={xAxisTickFormatter}
            domain={effectiveXDomainMemo || undefined}
            label={xAxisLabel}
          />

          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            interval={0}
            tick={yAxisTickRenderer}
            label={yAxisLabel}
          />

          <Tooltip content={tooltipContent} cursor={tooltipCursor} />

          {/* Highlight band for the focused row */}
          {highlightedRow &&
            (() => {
              const isLeading = highlightedRow.direction === 'leading'
              return (
                <ReferenceArea
                  y1={highlightedRow.name}
                  y2={highlightedRow.name}
                  fill={
                    isLeading ? 'rgba(5,150,105,0.18)' : 'rgba(220,38,38,0.15)'
                  }
                  stroke={
                    isLeading ? 'rgba(5,150,105,0.45)' : 'rgba(220,38,38,0.4)'
                  }
                  strokeWidth={1}
                  ifOverflow="extendDomain"
                />
              )
            })()}

          <Bar
            dataKey={totalValue}
            shape={stackedBarShape}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    ),
    [
      data,
      chartMargin,
      handleMouseMove,
      handleMouseLeave,
      xAxisTick,
      xAxisTickFormatter,
      effectiveXDomainMemo,
      xAxisLabel,
      yAxisWidth,
      yAxisTickRenderer,
      yAxisLabel,
      tooltipContent,
      tooltipCursor,
      highlightedRow,
      totalValue,
      stackedBarShape,
    ],
  )

  return (
    <ChartContainer ref={containerRef}>
      <HoveredCategoryContext.Provider value={hoveredCategory}>
        {chartElement}
      </HoveredCategoryContext.Provider>
    </ChartContainer>
  )
}

export const StackedBarChart = React.memo(StackedBarChartInner)
